import { useState } from 'react';
import Logo from '../../components/Logo';

/**
 * Codename selection. The candidate picks from a generated slate rather than typing
 * one, so the field cannot become a place where somebody writes their real name.
 */
export default function CodenameStep({
  positionTitle,
  options,
  onChoose,
  busy,
}: {
  positionTitle: string;
  options: string[];
  onChoose: (codename: string) => void;
  busy: boolean;
}) {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="mx-auto max-w-2xl p-6">
      <Logo size={28} className="mb-8" />
      <p className="text-xs uppercase tracking-widest text-ink/50">{positionTitle}</p>
      <h1 className="mt-2 text-2xl font-semibold">Choose your codename</h1>

      <p className="mt-4 text-sm text-ink/70">
        This screening is anonymous. We do not ask for your name or your email address, and we do
        not store them. Your codename is the only identifier attached to your results.
      </p>

      <div className="mt-6 rounded-md border-2 border-warn bg-warn/5 p-4">
        <p className="text-sm font-semibold text-warn">Write your codename down now.</p>
        <p className="mt-1 text-sm text-ink/80">
          Share it with the recruiters — it is the only way for you to refer to your results
          afterwards. It cannot be recovered later.
        </p>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {options.map((option) => {
          const active = selected === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => setSelected(option)}
              aria-pressed={active}
              className={[
                'rounded-md border px-4 py-3 text-left text-base font-medium transition-colors',
                active
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-ink/15 bg-surface hover:border-accent/50',
              ].join(' ')}
            >
              {option}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className="btn-primary mt-8 w-full sm:w-auto"
        disabled={!selected || busy}
        onClick={() => selected && onChoose(selected)}
      >
        {busy ? 'Confirming…' : selected ? `Continue as ${selected}` : 'Select a codename'}
      </button>
    </div>
  );
}
